import { type ReactNode, type RefObject, useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, type FieldPath, type Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import Loading from "../../../../components/Loading";
import { Checkbox } from "../../../../components/ui/checkbox";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { cn } from "../../../../lib/utils";
import {
  type AdministrativeAreaType,
  type AdministrativeAreaTypesModalOptions,
  type EntityId,
} from "../../types";

interface FormProps {
  administrativeAreaTypes?: AdministrativeAreaType[];
  buttonRef: RefObject<HTMLButtonElement | null>;
  initialValues?: Partial<AdministrativeAreaTypeFormValues>;
  isFetching?: boolean;
  modalOptions: AdministrativeAreaTypesModalOptions;
  onSubmit: (values: AdministrativeAreaTypeFormValues) => void;
}

interface FieldShellProps {
  children: ReactNode;
  className?: string;
  errorMessage?: string;
  id: string;
  label: string;
  required?: boolean;
}

interface ParentOption {
  label: string;
  selectValue: string;
  value: EntityId | null;
}

const NO_PARENT_SELECT_VALUE = "__no_parent__";
const EMPTY_VALUE = "-";

const entityIdSchema = z.union([z.string(), z.number()]);

export const createAdministrativeAreaTypeSchema = (
  requiredMessage: string,
  adminLevelMessage: string
) => {
  const adminLevelSchema = z
    .union([z.string(), z.number(), z.null(), z.undefined()])
    .transform(value => {
      if (value === "" || value === null || value === undefined) {
        return undefined;
      }

      return Number(value);
    })
    .refine(value => value === undefined || (Number.isFinite(value) && value >= 1 && value <= 4), {
      message: adminLevelMessage,
    })
    .optional();

  return z.object({
    active: z.boolean().default(true),
    admin_level: adminLevelSchema,
    country: entityIdSchema.nullish(),
    id: entityIdSchema.nullish(),
    name: z.preprocess(
      value => value ?? "",
      z.string().trim().min(1, { message: requiredMessage })
    ),
    parent: entityIdSchema.nullable().default(null),
  });
};

export type AdministrativeAreaTypeFormValues = z.infer<
  ReturnType<typeof createAdministrativeAreaTypeSchema>
>;

const DEFAULT_FORM_VALUES: AdministrativeAreaTypeFormValues = {
  active: true,
  name: "",
  parent: null,
};

const toErrorMessage = (error: unknown): string => {
  if (!error) {
    return "";
  }

  if (typeof error === "string") {
    return error;
  }

  if (typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === "string" ? message : "";
  }

  return String(error);
};

const getSelectValue = (value: EntityId | null | undefined) =>
  value === undefined || value === null ? NO_PARENT_SELECT_VALUE : String(value);

const normalizeInitialValues = (
  initialValues?: Partial<AdministrativeAreaTypeFormValues>
): AdministrativeAreaTypeFormValues => ({
  ...DEFAULT_FORM_VALUES,
  ...initialValues,
  active: initialValues?.active ?? DEFAULT_FORM_VALUES.active,
  admin_level: initialValues?.admin_level ?? undefined,
  name: initialValues?.name ?? DEFAULT_FORM_VALUES.name,
  parent: initialValues?.parent ?? DEFAULT_FORM_VALUES.parent,
});

// Collect the current node plus every type that descends from it, so none of
// them can be chosen as its parent (that would create a cycle the backend rejects).
const collectSelfAndDescendantIds = (
  administrativeAreaTypes: AdministrativeAreaType[],
  currentId: EntityId | null | undefined
): Set<string> => {
  const excluded = new Set<string>();
  if (currentId === null || currentId === undefined) {
    return excluded; // creating a new type: it has no descendants yet
  }

  // Map each parent id -> its direct children, for a breadth-first walk down the tree.
  const childrenByParent = new Map<string, AdministrativeAreaType[]>();
  for (const areaType of administrativeAreaTypes) {
    if (areaType.parent === null || areaType.parent === undefined) {
      continue;
    }
    const parentKey = String(areaType.parent);
    const siblings = childrenByParent.get(parentKey) ?? [];
    siblings.push(areaType);
    childrenByParent.set(parentKey, siblings);
  }

  const queue: string[] = [String(currentId)];
  while (queue.length > 0) {
    const id = queue.pop() as string;
    if (excluded.has(id)) {
      continue; // guards against malformed/looping data
    }
    excluded.add(id);
    for (const child of childrenByParent.get(id) ?? []) {
      if (child.id !== null && child.id !== undefined) {
        queue.push(String(child.id));
      }
    }
  }

  return excluded;
};

const createParentOptions = (
  administrativeAreaTypes: AdministrativeAreaType[],
  currentId: EntityId | null | undefined,
  noParentLabel: string
): ParentOption[] => {
  const excludedIds = collectSelfAndDescendantIds(administrativeAreaTypes, currentId);

  return [
    {
      label: noParentLabel,
      selectValue: NO_PARENT_SELECT_VALUE,
      value: null,
    },
    ...administrativeAreaTypes
      .filter(areaType => !excludedIds.has(String(areaType.id)))
      .map(areaType => ({
        label: areaType.name?.trim() || EMPTY_VALUE,
        selectValue: getSelectValue(areaType.id),
        value: areaType.id ?? null,
      })),
  ];
};

const FieldShell = ({
  children,
  className,
  errorMessage,
  id,
  label,
  required = false,
}: FieldShellProps) => (
  <div className={cn("space-y-2", className)}>
    <Label htmlFor={id} className="font-semibold text-foreground">
      {label}
      {required ? (
        <span aria-hidden="true" className="ml-0.5 text-destructive">
          *
        </span>
      ) : null}
    </Label>
    {children}
    {errorMessage ? (
      <p id={`${id}-error`} role="alert" className="text-destructive text-sm">
        {errorMessage}
      </p>
    ) : null}
  </div>
);

export const Form = ({
  administrativeAreaTypes = [],
  buttonRef,
  initialValues,
  isFetching = false,
  modalOptions,
  onSubmit,
}: FormProps) => {
  const { t } = useTranslation();
  const isViewMode = modalOptions.type === "view";
  const schema = useMemo(
    () =>
      createAdministrativeAreaTypeSchema(
        t("traderDetails.form.mandatoryFieldBlankError"),
        t("administrativeAreaTypes.adminLevelErrorMessage")
      ),
    [t]
  );
  const defaultValues = useMemo(() => normalizeInitialValues(initialValues), [initialValues]);
  const parentOptions = useMemo(
    () =>
      createParentOptions(
        administrativeAreaTypes,
        defaultValues.id,
        t("administrativeAreaTypes.noParent")
      ),
    [administrativeAreaTypes, defaultValues.id, t]
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<AdministrativeAreaTypeFormValues>({
    defaultValues,
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(schema) as Resolver<AdministrativeAreaTypeFormValues>,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, modalOptions.id, modalOptions.type, reset]);

  const getFieldError = (fieldName: FieldPath<AdministrativeAreaTypeFormValues>) =>
    toErrorMessage(errors[fieldName]?.message);

  if (isFetching) {
    return <Loading isVisible />;
  }

  return (
    <form className="space-y-5" noValidate onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        name="name"
        render={({ field }) => {
          const error = getFieldError("name");

          return (
            <FieldShell
              id="input-name"
              label={t("configuration.common.name")}
              errorMessage={error}
              required
            >
              <Input
                {...field}
                id="input-name"
                value={field.value ?? ""}
                disabled={isViewMode}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? "input-name-error" : undefined}
              />
            </FieldShell>
          );
        }}
      />

      <Controller
        control={control}
        name="parent"
        render={({ field }) => {
          const error = getFieldError("parent");

          return (
            <FieldShell
              id="input-parent"
              label={t("administrativeAreaTypes.parent")}
              errorMessage={error}
              required
            >
              <Select
                value={getSelectValue(field.value)}
                disabled={isViewMode}
                onValueChange={value => {
                  const selectedOption = parentOptions.find(option => option.selectValue === value);
                  field.onChange(selectedOption?.value ?? null);
                }}
              >
                <SelectTrigger
                  id="input-parent"
                  className="w-full"
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? "input-parent-error" : undefined}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {parentOptions.map(option => (
                    <SelectItem key={option.selectValue} value={option.selectValue}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldShell>
          );
        }}
      />

      <Controller
        control={control}
        name="admin_level"
        render={({ field }) => {
          const error = getFieldError("admin_level");

          return (
            <FieldShell
              id="input-admin-level"
              label={t("administrativeAreaTypes.adminLevel")}
              errorMessage={error}
            >
              <Input
                id="input-admin-level"
                type="number"
                min={1}
                max={4}
                value={field.value ?? ""}
                disabled={isViewMode}
                onBlur={field.onBlur}
                onChange={event => field.onChange(event.target.value)}
                name={field.name}
                ref={field.ref}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? "input-admin-level-error" : undefined}
              />
            </FieldShell>
          );
        }}
      />

      <Controller
        control={control}
        name="active"
        render={({ field }) => (
          <div className="flex items-center gap-2">
            <Checkbox
              id="active"
              checked={Boolean(field.value)}
              disabled={isViewMode}
              onCheckedChange={checked => field.onChange(checked === true)}
              onBlur={field.onBlur}
              name={field.name}
              ref={field.ref}
            />
            <Label htmlFor="active" className="font-semibold text-foreground">
              {t("configuration.common.active")}
            </Label>
          </div>
        )}
      />

      <button ref={buttonRef} type="submit" className="hidden" tabIndex={-1} aria-hidden="true" />
    </form>
  );
};
